import React from 'react';
import {
  FiCreditCard,
  FiClock,
  FiShield,
  FiRefreshCw,
  FiKey,
  FiCheckSquare,
  FiSmartphone,
} from 'react-icons/fi';
import { LuWallet } from "react-icons/lu";
import { PiBank } from "react-icons/pi";
import { FaStore, FaLaptop, FaTerminal } from 'react-icons/fa';

export function PaymentMethodsStep({
  selectedRegion,
  selectedCountry,
  selectedBusinessSector,  
  selectedMethods,
  selectedChannels,
  selectedCardSchemes,
  selectedWallets,
  selectedTerminalTypes,
  selectedEcommerceFeatures,
  selectedBnplProviders,
  selectedA2aProviders = [], // Add this new prop for A2A providers
  onToggleMethod,
  onToggleScheme,
  onToggleWallet,
  onToggleTerminalType,
  onToggleEcommerceFeature,
  onToggleBnplProvider,
  onToggleA2aProvider, // Add this new prop for A2A provider toggle
}) {
  // Debug logging
  console.log('PaymentMethodsStep - selectedChannels:', selectedChannels);
  console.log('PaymentMethodsStep - selectedMethods:', selectedMethods);
  console.log('PaymentMethodsStep - includes in-store:', selectedChannels?.includes('in-store'));
  console.log('PaymentMethodsStep - includes terminal:', selectedChannels?.includes('terminal'));

  // Determine which payment methods to display based on the selectedChannels
  const showEcommerceOnlyMethods = selectedChannels?.includes('ecommerce');
  
  // Define available payment methods based on selected channels
  const availablePaymentMethods = [
    // Card payments always available
    {
      id: 'card',
      label: 'Card Schemes',
      icon: <FiCreditCard className="w-6 h-6" />,
      description: 'Accept credit and debit cards',
    },
    ...(selectedChannels?.includes('unattended') || selectedChannels?.includes('in-store') ?
    [{

        id: 'terminal',
        label: 'Payment Terminal',
        icon: <LuWallet className="w-6 h-6" />,
        description: 'Attended and Unattended terminals',
      }

    ]:[]),
    
    // These methods are only shown when ecommerce is selected
    ...(showEcommerceOnlyMethods ? [
      {
        id: 'wallet',
        label: 'Digital Wallets',
        icon: <LuWallet className="w-6 h-6" />,
        description: 'Apple Pay, Google Pay, and more',
      },
      {
        id: 'bnpl',
        label: 'Buy Now, Pay Later',
        icon: <FiClock className="w-6 h-6" />,
        description: 'Flexible payment options',
      },
      {
        id: 'a2a',
        label: 'Account to Account',
        icon: <PiBank className="w-6 h-6" />,
        description: 'Pay using bank transfers',
      },
    ] : [])
  ];

  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      {/* Debug Info - Remove this in production 
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h4 className="font-semibold text-yellow-800 mb-2">Debug Info:</h4>
        <p className="text-sm text-yellow-700">
          Selected Channels: {JSON.stringify(selectedChannels)}
        </p>
        <p className="text-sm text-yellow-700">
          Includes 'in-store': {selectedChannels?.includes('in-store') ? 'YES' : 'NO'}
        </p>
        <p className="text-sm text-yellow-700">
          Includes 'terminal': {selectedChannels?.includes('terminal') ? 'YES' : 'NO'}
        </p>
        <p className="text-sm text-yellow-700">
          Selected Methods: {JSON.stringify(selectedMethods)}
        </p>
        <p className="text-sm text-yellow-700">
          Should show terminal section: {(selectedMethods.includes('card') && (selectedChannels?.includes('in-store') || selectedChannels?.includes('terminal'))) ? 'YES' : 'NO'}
        </p>
      </div>
        */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        { availablePaymentMethods.map((method) => (
          <button
            key={method.id}
            onClick={() => onToggleMethod(method.id)}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedMethods.includes(method.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-200'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div
                className={`p-2 rounded-full ${
                  selectedMethods.includes(method.id)
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {method.icon}
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900">{method.label}</h3>
                <p className="text-sm text-gray-500">{method.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {(selectedMethods.includes('card') && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FiCreditCard className="w-5 h-5 mr-2" />
            Card Schemes
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: 'visa', label: 'Visa' },
              { id: 'mastercard', label: 'Mastercard' },
              { id: 'amex', label: 'AMEX' },
              { id: 'discover', label: 'Discover' },
              { id: 'diners', label: 'Diners' },
              { id: 'jcb', label: 'JCB' },
              { id: 'upi', label: 'UPI' },
            ].map((scheme) => (
              <button
                key={scheme.id}
                onClick={() => onToggleScheme(scheme.id)}
                className={`p-3 rounded border ${
                  selectedCardSchemes.includes(scheme.id)
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                {scheme.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      {selectedMethods.includes('bnpl') && showEcommerceOnlyMethods && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FiClock className="w-5 h-5 mr-2" />
            BNPL Providers (e-Commerce)
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { id: 'klarna', label: 'Klarna', description: 'Popular in Europe' },
              { id: 'affirm', label: 'Affirm', description: 'US market leader' },
              { id: 'afterpay', label: 'Afterpay', description: 'Popular in Australia' },
              { id: 'clearpay', label: 'Clearpay', description: 'UK market presence' },
            ].map((provider) => (
              <button
                key={provider.id}
                onClick={() => onToggleBnplProvider(provider.id)}
                className={`p-4 rounded border text-left ${
                  selectedBnplProviders.includes(provider.id)
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="font-medium">{provider.label}</div>
                <div className="text-sm text-gray-500">{provider.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedMethods.includes('a2a') && showEcommerceOnlyMethods && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <PiBank className="w-5 h-5 mr-2" />
            Account to Account Providers (e-Commerce)
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { id: 'pay2', label: 'Pay2', description: 'Real-time bank transfers in Australia' },
              { id: 'fednow', label: 'FedNow', description: 'US real-time bank transfers' },
              { id: 'fasterpayments', label: 'Faster Payments', description: 'UK instant payments' },
              { id: 'sepa', label: 'SEPA', description: 'European payments' },
            ].map((provider) => (
              <button
                key={provider.id}
                onClick={() => onToggleA2aProvider ? onToggleA2aProvider(provider.id) : onToggleBnplProvider(provider.id)}
                className={`p-4 rounded border text-left ${
                  (selectedA2aProviders?.includes(provider.id) || selectedBnplProviders.includes(provider.id))
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="font-medium">{provider.label}</div>
                <div className="text-sm text-gray-500">{provider.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      

       

      { (selectedMethods.includes('terminal')) && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FaTerminal className="w-5 h-5 mr-2" />
           Terminal Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[             
              {
                id: 'payment-contactless-only',
                label: 'Contactless Card Reader',
                icon: FaLaptop,
                description: 'Contactless only card reader',
              },
              {
                id: 'card-insert-magstripe-pin-qrcode',
                label: 'Fully featured terminal',
                icon: FaTerminal,
                description: 'Insert, magstripe, pin pad, qrcode',
              }
            ].map((type) => (
              <button
                key={type.id}
                onClick={() => onToggleTerminalType(type.id)}
                className={`p-4 rounded border ${
                  selectedTerminalTypes.includes(type.id)
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <type.icon className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="font-medium">{type.label}</div>
                    <div className="text-sm text-gray-500">{type.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {(selectedMethods.includes('terminal') && selectedChannels?.includes('unattended')) && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FaTerminal className="w-5 h-5 mr-2" />
            Unattended Terminal - Integration Method
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[                           
              {
                id: 'unattended-terminal',
                label: 'Unattended Terminal',
                icon: FaTerminal,
                description: 'Unattended terminal',
              }
            ].map((type) => (
              <button
                key={type.id}
                onClick={() => onToggleTerminalType(type.id)}
                className={`p-4 rounded border ${
                  selectedTerminalTypes.includes(type.id)
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <type.icon className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="font-medium">{type.label}</div>
                    <div className="text-sm text-gray-500">{type.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="mt-2 p-2 bg-gray-50 rounded-lg">            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[             
                {
                  id: 'unattended-payment-contactless-only',
                  label: 'Contactless Card Reader',
                  icon: FaLaptop,
                  description: 'Contactless only card reader',
                },
                {
                  id: 'unattended-card-insert-magstripe-pin-qrcode',
                  label: 'Fully featured terminal',
                  icon: FaTerminal,
                  description: 'Insert, magstripe, pin pad, qrcode',
                }
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => onToggleTerminalType(type.id)}
                  className={`p-4 rounded border ${
                    selectedTerminalTypes.includes(type.id)
                      ? 'border-blue-500 bg-white'
                      : 'border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <type.icon className="w-5 h-5 text-gray-500" />
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-sm text-gray-500">{type.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {(selectedMethods.includes('terminal') && selectedChannels?.includes('in-store')) && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FaTerminal className="w-5 h-5 mr-2" />
            Attended Terminal - Integration Method
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                id: 'attended-terminal-pos-integrated',
                label: 'POS Integrated',
                icon: FaStore,
                description: 'POS integrated terminal',
              },             
              {
                id: 'attended-terminal-standalone',
                label: 'Standalone Terminal',
                icon: FaTerminal,
                description: 'Standalone terminal (no POS)',
              },              
            ].map((type) => (
              <button
                key={type.id}
                onClick={() => onToggleTerminalType(type.id)}
                className={`p-4 rounded border ${
                  selectedTerminalTypes.includes(type.id)
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <type.icon className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="font-medium">{type.label}</div>
                    <div className="text-sm text-gray-500">{type.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedMethods.includes('card') && selectedChannels?.includes('ecommerce') && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FaLaptop className="w-5 h-5 mr-2" />
            Fraud Protection Add-ons
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                id: 'visa-fraud-protection-solutions',
                label: 'Fraud Solutions',
                icon: FiRefreshCw,
                description: 'Visa advanced fraud detection services',
              },
              {
                id: 'tns-tokenization',
                label: 'TNS Tokenization',
                icon: FiKey,
                description: 'TNS proprietary tokenization',
              },
              {
                id: 'network-tokenization',
                label: 'Network Tokenization',
                icon: FiCheckSquare,
                description: 'Scheme tokens',
              },
              {
                id: '3dsecure',
                label: '3D Secure 2.0',
                icon: FiShield,
                description: 'Enhanced authentication',
              },
            ].map((feature) => (
              <button
                key={feature.id}
                onClick={() => onToggleEcommerceFeature(feature.id)}
                className={`p-4 rounded border ${
                  selectedEcommerceFeatures.includes(feature.id)
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <feature.icon className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="font-medium">{feature.label}</div>
                    <div className="text-sm text-gray-500">{feature.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedMethods.includes('wallet') && showEcommerceOnlyMethods && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <LuWallet className="w-5 h-5 mr-2" />
            Digital Wallets
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { id: 'applePay', label: 'Apple Pay' },
              { id: 'googlePay', label: 'Google Pay' },
              { id: 'paypal', label: 'PayPal' },
            ].map((wallet) => (
              <button
                key={wallet.id}
                onClick={() => onToggleWallet(wallet.id)}
                className={`p-3 rounded border ${
                  selectedWallets.includes(wallet.id)
                    ? 'border-blue-500 bg-white'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                {wallet.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}